import React, { useState, useEffect, useMemo } from 'react';
import { ApiRequest } from '../../../../../../../../../GlobalComponents/Builders';
import Select from 'react-select';

/**
 * CompositeValueSelect — Cascading or flat dropdown for composite value mappings.
 * 
 * Given a chain of resources (e.g., Category → Subcategory → Item),
 * renders either:
 *   - Cascading mode: N selects where each level filters based on parent selection
 *   - Flat mode: Single searchable dropdown with "Category > Subcategory > Item" display
 * 
 * Props:
 *   jobId            - The job ID
 *   sourceResourceId - The source resource ID (used to fetch chain config)
 *   value            - Current destination_resource_value_id (the leaf)
 *   onChange         - Callback with the selected leaf resource_value_id
 *   chainData        - Pre-fetched chain options (optional, will fetch if not provided)
 */
const CompositeValueSelect = ({ jobId, sourceResourceId, value, onChange, chainData: externalChainData, defaultMode = 'cascading', compact = false }) => {
    const [chainData, setChainData] = useState(externalChainData || null);
    const [loading, setLoading] = useState(!externalChainData);
    const [selections, setSelections] = useState([]);
    const [mode, setMode] = useState(defaultMode); // 'cascading' or 'flat'
    const [selectedValue, setSelectedValue] = useState(value || null);

    // Fetch chain options if not provided externally
    useEffect(() => {
        if (externalChainData) {
            setChainData(externalChainData);
            setLoading(false);
            return;
        }
        setLoading(true);
        ApiRequest('GET', '/dmt/jobs/{jobId}/valuemappings/chainOptions', {
            pathParams: { jobId },
            queryParams: { sourceResourceId },
        }).then(res => {
                let data = res?.data?.records || res?.data;
                if (typeof data === 'string') {
                    try { data = JSON.parse(data); } catch(e) { data = null; }
                }
                if (data) {
                    setChainData(data);
                }
            })
            .finally(() => setLoading(false));
    }, [jobId, sourceResourceId, externalChainData]);

    // When chainData loads or value changes, resolve the current value back up the tree
    useEffect(() => {
        if (!chainData || !selectedValue) {
            setSelections([]);
            return;
        }
        const path = findPathToValue(chainData.options, selectedValue);
        if (path) {
            setSelections(path);
        }
    }, [chainData, selectedValue]);

    // Sync from prop
    useEffect(() => {
        if (value !== undefined && value !== selectedValue) {
            setSelectedValue(value);
        }
    }, [value]);

    // Find the path from root to a specific resource_value_id
    const findPathToValue = (nodes, targetId) => {
        if (!nodes) return null;
        for (const node of nodes) {
            if (String(node.resource_value_id) === String(targetId)) {
                return [node.resource_value_id];
            }
            if (node.children && node.children.length > 0) {
                const childPath = findPathToValue(node.children, targetId);
                if (childPath) {
                    return [node.resource_value_id, ...childPath];
                }
            }
        }
        return null;
    };

    // Build flat options list with display chains (for flat mode)
    const flatOptions = useMemo(() => {
        if (!chainData) return [];
        const options = [];

        const buildFlat = (nodes, pathLabels = []) => {
            for (const node of nodes) {
                const currentPath = [...pathLabels, node.value];
                // Add this node as an option (it's a valid terminal selection)
                options.push({
                    value: node.resource_value_id,
                    label: currentPath.join(' > '),
                });
                // Recurse into children
                if (node.children && node.children.length > 0) {
                    buildFlat(node.children, currentPath);
                }
            }
        };

        buildFlat(chainData.options || []);
        return options;
    }, [chainData]);

    // Get options for a given level based on current selections (cascading mode)
    const getOptionsForLevel = (level) => {
        if (!chainData) return [];
        let nodes = chainData.options;
        for (let i = 0; i < level; i++) {
            if (!selections[i]) return [];
            const selected = nodes.find(n => String(n.resource_value_id) === String(selections[i]));
            if (!selected) return [];
            nodes = selected.children || [];
        }
        return nodes;
    };

    const handleLevelChange = (level, selectedOption) => {
        const newSelections = [...selections.slice(0, level)];
        if (selectedOption) {
            newSelections[level] = selectedOption.value;
        }
        setSelections(newSelections);

        const deepestValue = newSelections[newSelections.length - 1] || null;
        setSelectedValue(deepestValue);
        if (onChange) {
            onChange(deepestValue);
        }
    };

    const handleFlatChange = (selectedOption) => {
        const val = selectedOption ? selectedOption.value : null;
        setSelectedValue(val);
        if (onChange) {
            onChange(val);
        }
        // Also update selections so switching to cascading mode shows the right state
        if (val && chainData) {
            const path = findPathToValue(chainData.options, val);
            if (path) setSelections(path);
        } else {
            setSelections([]);
        }
    };

    if (loading) return <span className="text-muted">Loading...</span>;
    if (!chainData) return <span className="text-danger">No chain config</span>;

    const chainLevels = chainData.chain || [];

    const fontSize = compact ? '12px' : '13px';
    const selectStyles = {
        menuPortal: base => ({ ...base, zIndex: 9999 }),
        menu: base => ({ ...base, fontSize }),
        control: base => ({ ...base, minHeight: compact ? '30px' : '32px', fontSize }),
        valueContainer: base => ({ ...base, padding: '0 6px' }),
        singleValue: base => ({ ...base, fontSize }),
        option: base => ({ ...base, fontSize, padding: compact ? '4px 8px' : base.padding }),
        input: base => ({ ...base, margin: '0', padding: '0' }),
    };

    return (
        <div className="composite-value-select" style={{ width: compact ? '100%' : '85%' }}>
            <div className="mb-2">
                <small
                    className="txt-secondary"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setMode(mode === 'cascading' ? 'flat' : 'cascading')}
                >
                    {mode === 'cascading' ? '⊞ Flat view' : '⊟ Cascading view'}
                </small>
            </div>

            {mode === 'flat' ? (
                <Select
                    options={flatOptions}
                    value={flatOptions.find(o => String(o.value) === String(selectedValue)) || null}
                    onChange={handleFlatChange}
                    isClearable
                    placeholder="Search destination..."
                    className="react-select-container"
                    classNamePrefix="react-select"
                    menuPortalTarget={compact ? undefined : document.body}
                    menuPlacement="auto"
                    styles={selectStyles}
                />
            ) : (
                chainLevels.map((level, idx) => {
                    const options = getOptionsForLevel(idx);
                    if (idx > 0 && !selections[idx - 1]) return null;
                    if (options.length === 0 && idx > 0) return null;

                    const currentValue = selections[idx] || null;
                    const selectOptions = options.map(opt => ({
                        value: opt.resource_value_id,
                        label: opt.value,
                    }));

                    const selectedOption = selectOptions.find(o => String(o.value) === String(currentValue)) || null;

                    return (
                        <div key={idx} className="mb-1">
                            <label className="form-label small text-muted mb-0">{level.label}</label>
                            <Select
                                options={selectOptions}
                                value={selectedOption}
                                onChange={(opt) => handleLevelChange(idx, opt)}
                                isClearable={idx > 0}
                                placeholder={`Select ${level.label}...`}
                                className="react-select-container"
                                classNamePrefix="react-select"
                                menuPortalTarget={compact ? undefined : document.body}
                                menuPlacement="auto"
                                styles={selectStyles}
                            />
                        </div>
                    );
                })
            )}
        </div>
    );
};

export default CompositeValueSelect;
